// - - - - - - - - - -
// #175 (Option B, 2026-06-03) — merge the carpet + hard-surface filler-job
// recommendations for an installer configured for BOTH surfaces.
//
// Brandon's call (#175): a "does-both" installer (Hard Surface classification
// with non-zero carpet capacity — e.g. Sergio Osorio at ATL) should see BOTH
// a carpet section and a hard-surface section in the Capacity Recommendations
// pop-up, with the per-tier recommendation lists pulled from both surfaces.
//
// `recommendFillerCarpetJobs` and `recommendFillerHardSurfaceJobs` return the
// identical shape — { expediteJobs, rtsJobs, alreadyScheduledJobs, repairJobs,
// totalCount } — so merging is a per-tier concat with de-duplication.
//
// Ordering: carpet entries first, then hard-surface entries, each already
// sorted within its own surface by the recommenders' closest-fit compare. We
// deliberately do NOT re-sort across surfaces — carpet sizes are in yards and
// hard-surface sizes are in square feet, so a cross-surface numeric sort would
// be comparing apples to oranges. Grouping carpet-then-hard keeps the list
// predictable, and each hard-surface row already carries a [Type] tag so the
// two surfaces stay visually distinguishable.
//
// De-dup: a single job can match both recommenders (e.g. an invoice with both
// carpet and a hard-surface main-SKU line item). We key on `_id` (falling back
// to `invoiceNo`) and keep the first occurrence — carpet wins ties since it is
// concatenated first. Pure function; unit-tested directly.

const TIERS = ["expediteJobs", "rtsJobs", "alreadyScheduledJobs", "repairJobs"];

const entryKey = (entry) =>
  String(entry?._id ?? entry?.invoiceNo ?? "");

/**
 * Merge two recommendation result objects (carpet + hard surface) into one.
 *
 * @param {Object|null} carpetRecs - output of recommendFillerCarpetJobs
 * @param {Object|null} hardRecs   - output of recommendFillerHardSurfaceJobs
 * @returns {{expediteJobs, rtsJobs, alreadyScheduledJobs, repairJobs, totalCount}}
 */
export function mergeFillerRecommendations(carpetRecs, hardRecs) {
  const out = {};
  let totalCount = 0;

  for (const tier of TIERS) {
    const carpetTier = carpetRecs?.[tier] || [];
    const hardTier = hardRecs?.[tier] || [];
    const seen = new Set();
    const merged = [];
    for (const entry of [...carpetTier, ...hardTier]) {
      const key = entryKey(entry);
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      merged.push(entry);
    }
    out[tier] = merged;
    totalCount += merged.length;
  }

  out.totalCount = totalCount;
  return out;
}

export default mergeFillerRecommendations;
